<script setup>
const props = defineProps({
  show: Boolean,
  title: { type: String, default: "Mensaje" },
  message: { type: String, default: "" },
  html: { type: String, default: "" },
  confirmText: { type: String, default: "Entendido" },
  type: { type: String, default: "info" }, // success, error, warning, info
  autoClose: { type: Number, default: 0 }, // tiempo en ms, 0 = no auto-cerrar
});

const emit = defineEmits(["close"]);

function onClose() {
  emit("close");
}

// Auto-cerrar si autoClose > 0
if (props.autoClose > 0) {
  setTimeout(() => {
    if (props.show) {
      onClose();
    }
  }, props.autoClose);
}
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="onClose">
      <div class="modal-container">
        <div class="modal-content">
          <div class="icon-wrapper" :class="type">
            <span class="material-symbols-rounded icon">
              {{ 
                type === 'success' ? 'check_circle' : 
                type === 'error' ? 'error' : 
                type === 'warning' ? 'warning' : 
                'info' 
              }}
            </span>
          </div>
          <h2 class="modal-title" :class="type">{{ title }}</h2>
          <div v-if="html" class="modal-message html-content" v-html="html"></div>
          <p v-else-if="message" class="modal-message">{{ message }}</p>
          
          <div class="modal-actions">
            <button class="btn-confirm" :class="type" @click="onClose">{{ confirmText }}</button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Reuse generic modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  position: relative;
  background: var(--panel-bg, #1e293b);
  border: 1.5px solid var(--panel-border, #334155);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  color: var(--text, #f1f5f9);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.modal-content {
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

/* Icons */
.icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
}

.icon-wrapper.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
}

.icon-wrapper.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3);
}

.icon-wrapper.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
}

.icon-wrapper.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
}

.icon {
  font-size: 2.5rem;
  color: #fff;
}

/* Typography */
.modal-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
}

.modal-title.success { color: #34d399; }
.modal-title.error { color: #f87171; }
.modal-title.warning { color: #fbbf24; }
.modal-title.info { color: #60a5fa; }

.modal-message {
  color: var(--muted, #94a3b8);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
}

.html-content {
  text-align: left;
  width: 100%;
  background: rgba(255,255,255,0.05);
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* Buttons */
.modal-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
}

button {
  flex: 1;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.btn-confirm.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}
.btn-confirm.success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
}

.btn-confirm.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}
.btn-confirm.error:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

.btn-confirm.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}
.btn-confirm.warning:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.4);
}

.btn-confirm.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
.btn-confirm.info:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
}

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
